<script setup>
import { ref, onMounted } from 'vue'
import SettingsLayout from '@/Layouts/SettingsLayout.vue'
import SettingsPersonalDetailsForm from '@/Components/Forms/SettingsPersonalDetailsForm.vue'

const props = defineProps({
  user: Object,
})

const personalDetails = ref({
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  date_of_birth: '',
  address1: '',
  city: '',
  zip: '',
  country: 'United Kingdom',
  timezone: 'Europe/London',
})

const personalDetailsRef = ref(null)
const loading = ref(false)
const errors = ref({})
const successMessage = ref('')

// Format date to YYYY-MM-DD for HTML date input
const formatDateForInput = (dateString) => {
  if (!dateString) return ''
  // Handle ISO 8601 format from Laravel (e.g., "1984-01-12T00:00:00.000000Z")
  const date = new Date(dateString)
  if (isNaN(date.getTime())) return ''
  return date.toISOString().split('T')[0]
}

// Populate from user prop
onMounted(() => {
  if (props.user) {
    // Split name into first_name and last_name
    const nameParts = (props.user.name || '').split(' ')
    const firstName = nameParts[0] || ''
    const lastName = nameParts.slice(1).join(' ') || ''

    // Get address from user's addresses (first address if exists)
    const address = props.user.addresses && props.user.addresses.length > 0
      ? props.user.addresses[0]
      : null

    personalDetails.value = {
      first_name: firstName,
      last_name: lastName,
      email: props.user.email || '',
      phone: props.user.phone || '',
      date_of_birth: formatDateForInput(props.user.date_of_birth),
      address1: address?.address1 || '',
      city: address?.city || '',
      zip: address?.zip || '',
      country: address?.country || 'United Kingdom',
      timezone: props.user.timezone || 'Europe/London',
    }
  }
})

const updatePersonalDetails = (newDetails) => {
  personalDetails.value = newDetails
}

const savePersonalInfo = async () => {
  if (!personalDetailsRef.value.validate()) {
    return
  }

  loading.value = true
  errors.value = {}
  successMessage.value = ''

  try {
    // Combine first_name and last_name into name (email is read-only and not sent)
    const payload = {
      name: `${personalDetails.value.first_name} ${personalDetails.value.last_name}`.trim(),
      phone: personalDetails.value.phone,
      date_of_birth: personalDetails.value.date_of_birth,
      timezone: personalDetails.value.timezone,
      address1: personalDetails.value.address1,
      city: personalDetails.value.city,
      zip: personalDetails.value.zip,
      country: personalDetails.value.country,
    }

    const response = await fetch('/teacher/settings/personal-info', {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json',
        'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content')
      },
      body: JSON.stringify(payload)
    })

    const data = await response.json()

    if (!response.ok) {
      if (data.errors) {
        errors.value = data.errors
      } else {
        errors.value = { general: data.message || 'Failed to save. Please try again.' }
      }
      loading.value = false
      return
    }

    successMessage.value = 'Personal information updated successfully!'
    loading.value = false

    // Clear success message after 3 seconds
    setTimeout(() => {
      successMessage.value = ''
    }, 3000)

  } catch (err) {
    errors.value = { general: 'An error occurred. Please try again.' }
    loading.value = false
  }
}
</script>

<template>
  <SettingsLayout>
    <div class="space-y-6">
      <!-- Success Message -->
      <div v-if="successMessage" class="bg-green-50 border-2 border-green-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-green-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">check_circle</span>
        </div>
        <div>
          <p class="text-green-900 font-semibold">Success</p>
          <p class="text-green-700 text-sm">{{ successMessage }}</p>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="errors.general" class="bg-red-50 border-2 border-red-200 rounded-2xl p-5 flex items-center">
        <div class="flex-shrink-0 w-10 h-10 bg-red-500 rounded-full flex items-center justify-center mr-4">
          <span class="material-symbols-outlined text-white text-xl">error</span>
        </div>
        <div>
          <p class="text-red-900 font-semibold">Error</p>
          <p class="text-red-700 text-sm">{{ errors.general }}</p>
        </div>
      </div>

      <!-- Personal Details Form -->
      <SettingsPersonalDetailsForm
        ref="personalDetailsRef"
        :model-value="personalDetails"
        @update:model-value="updatePersonalDetails"
        :errors="errors"
        title="Personal Information"
        description="Update your personal details and contact information"
        :field-mapping="{ address: 'address1', postcode: 'zip' }"
      />

      <!-- Save Button -->
      <div class="flex justify-end pt-6 border-t border-gray-200">
        <button
          @click="savePersonalInfo"
          :disabled="loading"
          class="px-8 py-3.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-bold hover:from-blue-700 hover:to-blue-800 transition-all shadow-xl shadow-blue-500/40 hover:shadow-2xl hover:shadow-blue-600/50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center text-base"
        >
          <span v-if="loading" class="material-symbols-outlined animate-spin mr-2 text-xl">progress_activity</span>
          {{ loading ? 'Saving...' : 'Save Changes' }}
          <span v-if="!loading" class="material-symbols-outlined ml-2 text-xl">check</span>
        </button>
      </div>
    </div>
  </SettingsLayout>
</template>
